/**
 * Token store + reactive auth state.
 *
 * Sprint 7 — Trust Boundary. The admin UI holds two tokens:
 *   - access  (short-lived, ~15 min) — attached to every HTTP request as
 *     `Authorization: Bearer ...`, and to WS via Sec-WebSocket-Protocol
 *   - refresh (long-lived, ~30 days) — used silently when an access token
 *     expires
 *
 * Persistence: localStorage. We accept the standard XSS exposure risk in
 * exchange for the convenience of "refresh the tab, stay logged in"; in
 * production deployments behind reverse proxies, callers may switch to
 * HttpOnly cookies — the upgrade path is isolated to this file.
 *
 * Decoding: we never trust the access token client-side for authorization
 * decisions; the server is the source of truth. We DO decode it to read
 * tenant_id, scopes, and expiry — used purely for UI affordances (showing
 * the user's name, hiding admin nav, scheduling the refresh).
 */

const ACCESS_KEY  = "vseh.access_token";
const REFRESH_KEY = "vseh.refresh_token";
const SCOPES_KEY  = "vseh.scopes";


export interface AuthClaims {
  sub:        string;
  tenant_id:  string;
  scopes:     string[];
  actor_type: string;
  session_id: string;
  exp:        number;
}


export interface AuthState {
  accessToken:  string;
  refreshToken: string;
  claims:       AuthClaims;
}


type Listener = (state: AuthState | null) => void;
const listeners = new Set<Listener>();


/** Subscribe to auth-state changes; returns an unsubscribe fn. */
export function subscribeAuth(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}


function notify(state: AuthState | null): void {
  for (const fn of listeners) fn(state);
}


/** Decode JWT payload without verifying — that's the server's job. */
export function decodeJwtPayload(token: string): AuthClaims | null {
  try {
    const [, payloadB64] = token.split(".");
    if (!payloadB64) return null;
    // base64url → base64 → JSON
    const b64 = payloadB64.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), "=");
    const raw = atob(padded);
    const json = JSON.parse(decodeURIComponent(escape(raw))) as Record<string, unknown>;
    return {
      sub:        String(json.sub ?? ""),
      tenant_id:  String(json.vseh_tenant_id ?? ""),
      scopes:     Array.isArray(json.vseh_scopes) ? (json.vseh_scopes as string[]) : [],
      actor_type: String(json.vseh_actor_type ?? "human"),
      session_id: String(json.vseh_session_id ?? ""),
      exp:        Number(json.exp ?? 0),
    };
  } catch {
    return null;
  }
}


export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}


export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}


export function getCurrentAuth(): AuthState | null {
  const access  = getAccessToken();
  const refresh = getRefreshToken();
  if (!access || !refresh) return null;
  const claims = decodeJwtPayload(access);
  if (!claims) return null;
  return { accessToken: access, refreshToken: refresh, claims };
}


export function setAuth(accessToken: string, refreshToken: string): AuthState | null {
  const claims = decodeJwtPayload(accessToken);
  if (!claims) return null;
  localStorage.setItem(ACCESS_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
  localStorage.setItem(SCOPES_KEY, JSON.stringify(claims.scopes));
  const state: AuthState = { accessToken, refreshToken, claims };
  notify(state);
  return state;
}


export function clearAuth(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(SCOPES_KEY);
  notify(null);
}


/** True if the user holds the named scope (string match). */
export function hasScope(scope: string): boolean {
  const auth = getCurrentAuth();
  return auth?.claims.scopes.includes(scope) ?? false;
}


/** Seconds until the access token expires. Negative if already expired. */
export function secondsUntilExpiry(): number {
  const auth = getCurrentAuth();
  if (!auth) return -1;
  return auth.claims.exp - Math.floor(Date.now() / 1000);
}
